// @ts-nocheck
/**
 * ObjectionsScreen — Objections (#31, audit B2-objections, W4). « Bêta ».
 *
 * Responsibility: what blocks the prospects, and what unblocks them.
 * - Data: the transcribed calls of the period, read page by page from
 *   `POST /api/entities/ParallelCall/page` (filter: period, transcript,
 *   selected customer), up to ANALYSIS_CAP calls. It used to read the 500
 *   calls the dialer had loaded, whatever the period (T-d3). Past the cap,
 *   the screen says how many calls it left out.
 * - Detection: still by patterns, in the browser (`src/lib/objections.ts`):
 *   no server-side analysis exists yet. The screen says so (« Bêta ») in
 *   one line instead of a paragraph (audit: « l'assumer en bêta »).
 * - Top: four VFStatCards over every objection of the period (a failed
 *   load shows « Indisponible », a zero denominator « — »; no trend, there
 *   is no previous period to compare with). Then the bar chart per type,
 *   shadcn Selects, and a DataTable with a player on each row that starts
 *   where the objection was heard.
 * Docs: docs/refonte-ui/audit-ui-ux.md (Objections), DESIGN.md §4, §5.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDialerDerived } from '@/contexts/DialerDerivedContext';
import { useDialerData } from '@/contexts/DialerDataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VFStatCard } from '@/components/vf/StatCard';
import { DataTable } from '@/components/shared/DataTable';
import { RecordingCell } from '@/components/dialer/RecordingPlayer';
import { formatDate } from '@/i18n/locale';
import { detecterObjections, calculerStats, parFamille, FAMILLES } from '@/lib/objections';
import { toCsv, downloadCsv } from '@/lib/exportCsv';
import { ApiErrorException } from '@/api/client';
import { fetchAllPages, fetchEntityPage } from '@/api/endpoints/entityPage';

/** At most this many transcribed calls are analysed; past it the screen says so. */
export const ANALYSIS_CAP = 1000;
const PAGE_SIZE = 50;

const STATUTS = [
  { cle: 'passee', libelleKey: 'pages:objections.status.passed', classe: 'bg-green-100 text-green-800 border-green-200' },
  { cle: 'non_passee', libelleKey: 'pages:objections.status.notPassed', classe: 'bg-red-100 text-red-800 border-red-200' },
  { cle: 'indetermine', libelleKey: 'pages:objections.status.undetermined', classe: 'bg-muted text-muted-foreground border-border' },
];
const libelleStatut = (c, t) => { const s = STATUTS.find((x) => x.cle === c); return s ? t(s.libelleKey) : c; };
const classeStatut = (c) => STATUTS.find((s) => s.cle === c)?.classe || '';

const PERIODES = [
  { cle: '7', libelleKey: 'pages:objections.period.last7' },
  { cle: '30', libelleKey: 'pages:objections.period.last30' },
  { cle: '90', libelleKey: 'pages:objections.period.last90' },
  { cle: 'all', libelleKey: 'pages:objections.period.all' },
];
export const DEFAULT_OBJECTIONS_PERIOD = '30';

/** « 01:12 » → 72 s: the player starts where the objection was heard. */
export function secondsOf(horodatage) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(horodatage || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : undefined;
}

/** The server filter of the analysed calls: the period, a transcript, the selected customer. */
export function objectionsFilter(period, customerId, now = new Date()) {
  const and = [{ transcription: { $nin: ['', null] } }];
  if (period !== 'all') {
    const since = new Date(now);
    since.setDate(since.getDate() - Number(period));
    since.setHours(0, 0, 0, 0);
    and.push({ created_date: { $gte: since.toISOString() } });
  }
  if (customerId) and.push({ customer_id: customerId });
  return { $and: and };
}

const rate = (n, d) => (d > 0 ? n / d : null);
const ALL = '__all__';

export default function ObjectionsScreen() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { prospectsBySource = {} } = useDialerDerived();
  const { selectedCustomerId } = useDialerData();

  const [fPeriode, setFPeriode] = useState(DEFAULT_OBJECTIONS_PERIOD);
  const [fType, setFType] = useState(ALL);
  const [fStatut, setFStatut] = useState(ALL);
  const [fAgent, setFAgent] = useState(ALL);
  const [fListe, setFListe] = useState(ALL);
  const [dureeMin, setDureeMin] = useState('');
  const [dureeMax, setDureeMax] = useState('');
  const [page, setPage] = useState(0);

  const famille = (cle) => t(`pages:objections.families.${cle}`);

  // The period is a server filter; it is computed once per change so the
  // query key stays the same between renders.
  const filter = useMemo(() => objectionsFilter(fPeriode, selectedCustomerId), [fPeriode, selectedCustomerId]);
  const analysis = useQuery({
    queryKey: ['parallel-calls', 'objections', filter],
    queryFn: ({ signal }) => fetchAllPages('ParallelCall', { filter, sort: '-created_date' }, { cap: ANALYSIS_CAP, signal }),
    staleTime: 60_000,
  });
  // Every call of the period, transcribed or not (« N appels au total »).
  const totalFilter = useMemo(() => {
    const and = filter.$and.filter((c) => !('transcription' in c));
    return and.length ? { $and: and } : {};
  }, [filter]);
  const allCalls = useQuery({
    queryKey: ['parallel-calls', 'objections-total', totalFilter],
    queryFn: ({ signal }) => fetchEntityPage('ParallelCall', { filter: totalFilter, limit: 1, include_total: true }, signal),
    staleTime: 60_000,
  });

  const calls = analysis.data?.items ?? [];
  const listByPhone = useMemo(() => {
    const m = new Map();
    Object.entries(prospectsBySource).forEach(([src, ps]) => (ps || []).forEach((p) => { if (p.prospect_phone && !m.has(p.prospect_phone)) m.set(p.prospect_phone, src); }));
    return m;
  }, [prospectsBySource]);

  // Detection over every analysed call, once; the filters apply after.
  const toutes = useMemo(
    () => calls.flatMap((a) => detecterObjections({ ...a, list_name: listByPhone.get(a.prospect_phone) || null }).map((o, i) => ({ ...o, key: `${a.id}-${i}` }))),
    [calls, listByPhone],
  );
  const agents = useMemo(() => [...new Set(toutes.map((o) => o.agent).filter((a) => a && a !== '—'))].sort(), [toutes]);
  const listes = useMemo(() => Object.keys(prospectsBySource).sort(), [prospectsBySource]);

  const filtrees = useMemo(() => toutes.filter((o) => {
    if (fType !== ALL && o.famille !== fType) return false;
    if (fStatut !== ALL && o.statut !== fStatut) return false;
    if (fAgent !== ALL && o.agent !== fAgent) return false;
    if (fListe !== ALL && o.liste !== fListe) return false;
    if (dureeMin !== '' && o.dureeSecondes < Number(dureeMin)) return false;
    if (dureeMax !== '' && o.dureeSecondes > Number(dureeMax)) return false;
    return true;
  }), [toutes, fType, fStatut, fAgent, fListe, dureeMin, dureeMax]);
  // Back to the first page when the filters change.
  const filterKey = [fPeriode, fType, fStatut, fAgent, fListe, dureeMin, dureeMax].join('|');
  React.useEffect(() => { setPage(0); }, [filterKey]);

  // The tiles stay global over the period (#31: « EN HAUT : stats globales »).
  const stats = useMemo(() => calculerStats(calls, toutes), [calls, toutes]);
  const familles = useMemo(() => parFamille(filtrees), [filtrees]);
  const maxBarre = Math.max(1, ...familles.map((f) => f.total));

  const tileState = analysis.isPending ? 'loading' : analysis.isError ? 'error' : 'ready';
  const periodHint = t(PERIODES.find((p) => p.cle === fPeriode)?.libelleKey ?? 'pages:objections.period.all');

  const pills = [];
  if (fType !== ALL) pills.push({ id: 'type', label: famille(fType), onRemove: () => setFType(ALL) });
  if (fStatut !== ALL) pills.push({ id: 'status', label: libelleStatut(fStatut, t), onRemove: () => setFStatut(ALL) });
  if (fAgent !== ALL) pills.push({ id: 'user', label: fAgent, onRemove: () => setFAgent(ALL) });
  if (fListe !== ALL) pills.push({ id: 'list', label: fListe, onRemove: () => setFListe(ALL) });
  if (dureeMin !== '') pills.push({ id: 'min', label: `≥ ${dureeMin} s`, onRemove: () => setDureeMin('') });
  if (dureeMax !== '') pills.push({ id: 'max', label: `≤ ${dureeMax} s`, onRemove: () => setDureeMax('') });
  const clearFilters = () => { setFType(ALL); setFStatut(ALL); setFAgent(ALL); setFListe(ALL); setDureeMin(''); setDureeMax(''); };

  const exporter = () => {
    const entetes = ['agent', 'contact', 'company', 'type', 'objection', 'answer', 'status', 'timestamp', 'date'].map((k) => t(`pages:objections.csv.${k}`));
    const lignes = filtrees.map((o) => [
      o.agent, o.contact, o.entreprise, famille(o.famille),
      o.resume, o.reponse || '', libelleStatut(o.statut, t), o.horodatage || '',
      o.dateAppel ? formatDate(o.dateAppel) : '',
    ]);
    downloadCsv(`objections-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(entetes, lignes));
  };

  const byId = useMemo(() => new Map(calls.map((c) => [c.id, c])), [calls]);
  const columns = [
    { id: 'user', header: t('pages:objections.table.columns.user'), hideable: true, cell: (o) => <span className="text-xs whitespace-nowrap">{o.agent}</span> },
    {
      id: 'contact', header: t('pages:objections.table.columns.contact'),
      cell: (o) => (
        <div>
          <p className="font-medium leading-tight">{o.contact}</p>
          <p className="text-xs text-muted-foreground leading-tight">{o.entreprise}</p>
        </div>
      ),
    },
    {
      id: 'type', header: t('pages:objections.table.columns.type'),
      cell: (o) => {
        const f = FAMILLES.find((x) => x.cle === o.famille);
        return <span className="text-xs px-2 py-0.5 rounded-full border whitespace-nowrap" style={{ borderColor: f?.couleur, color: f?.couleur }}>{famille(o.famille)}</span>;
      },
    },
    { id: 'objection', header: t('pages:objections.table.columns.objection'), cell: (o) => <span className="text-xs block max-w-[280px]">{o.resume}</span> },
    { id: 'answer', header: t('pages:objections.table.columns.answer'), hideable: true, cell: (o) => <span className="text-xs text-muted-foreground block max-w-[280px]">{o.reponse || '—'}</span> },
    { id: 'status', header: t('pages:objections.table.columns.status'), cell: (o) => <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${classeStatut(o.statut)}`}>{libelleStatut(o.statut, t)}</span> },
    { id: 'timestamp', header: t('pages:objections.table.columns.timestamp'), hideable: true, cell: (o) => <span className="text-xs font-mono text-muted-foreground">{o.horodatage || '—'}</span> },
    { id: 'date', header: t('pages:objections.table.columns.date'), hideable: true, cell: (o) => <span className="text-xs text-muted-foreground whitespace-nowrap">{o.dateAppel ? formatDate(o.dateAppel) : '—'}</span> },
    {
      id: 'recording', header: t('pages:objections.table.columns.recording'),
      cell: (o) => (o.enregistrement
        ? <RecordingCell call={byId.get(o.callId) ?? { recording_url: o.enregistrement }} compact startAt={secondsOf(o.horodatage)} />
        : <span className="text-xs text-muted-foreground/50">—</span>),
    },
  ];
  const rowsPage = filtrees.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const errorCode = analysis.error instanceof ApiErrorException ? analysis.error.code : analysis.error ? 'NETWORK' : undefined;
  const capped = analysis.data?.capped;
  const selectCls = 'h-9 w-auto min-w-40';

  return (
    <div className="space-y-4" data-testid="ecran-objections">
      <p className="flex flex-wrap items-center gap-2 text-[12px]" style={{ color: 'var(--vf-text-2)' }} data-testid="objections-beta">
        <span className="rounded-full border px-2 py-0.5 text-[11px] font-semibold" style={{ borderColor: 'var(--vf-accent-bd)', background: 'var(--vf-accent-bg)', color: 'var(--vf-text-1)' }}>
          {t('pages:objections.beta.badge')}
        </span>
        {t('pages:objections.beta.body')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={fPeriode} onValueChange={setFPeriode}>
          <SelectTrigger className={selectCls} aria-label={t('pages:objections.filters.period')}><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODES.map((p) => <SelectItem key={p.cle} value={p.cle}>{t(p.libelleKey)}</SelectItem>)}</SelectContent>
        </Select>
        {capped && (
          <span role="status" className="text-[12px]" style={{ color: 'var(--vf-text-2)' }} data-testid="objections-plafond">
            {t('pages:objections.capped', { count: calls.length, total: analysis.data.total })}
          </span>
        )}
      </div>

      {filtrees.length !== toutes.length && (
        <p className="text-[11px] text-muted-foreground -mb-1">{t('pages:objections.globalNotice')}</p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="objections-stats">
        <VFStatCard
          state={tileState}
          label={t('pages:objections.tiles.callsWithTranscript')}
          metric={{ current: analysis.data ? stats.appelsConnectes : null, kind: 'count' }}
          hint={allCalls.data && typeof allCalls.data.total === 'number' ? `${periodHint} · ${t('pages:objections.tiles.callsTotal', { count: allCalls.data.total })}` : periodHint}
        />
        <VFStatCard state={tileState} label={t('pages:objections.tiles.callsWithObjection')} metric={{ current: rate(stats.appelsAvecObjection, stats.appelsConnectes), kind: 'rate' }}
          hint={`${periodHint} · ${t('pages:objections.tiles.outOf', { count: stats.appelsAvecObjection, total: stats.appelsConnectes })}`} />
        <VFStatCard state={tileState} label={t('pages:objections.tiles.answered')} metric={{ current: rate(stats.objectionsRepondues, stats.objections), kind: 'rate' }}
          hint={`${periodHint} · ${t('pages:objections.tiles.outOf', { count: stats.objectionsRepondues, total: stats.objections })}`} />
        <VFStatCard state={tileState} label={t('pages:objections.tiles.passed')} metric={{ current: rate(stats.objectionsPassees, stats.objections), kind: 'rate' }}
          hint={`${periodHint} · ${t('pages:objections.tiles.outOf', { count: stats.objectionsPassees, total: stats.objections })}`} />
      </div>

      <section className="rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }} data-testid="objections-graphique" aria-labelledby="objections-chart-title">
        <h2 id="objections-chart-title" className="text-sm font-semibold mb-3" style={{ color: 'var(--vf-text-1)' }}>
          {t('pages:objections.chart.title')}
          {pills.length > 0 && <span className="ml-2 text-[11px] font-normal text-muted-foreground">{t('pages:objections.chart.onCurrentFilter')}</span>}
        </h2>
        {filtrees.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{t('pages:objections.chart.empty')}</p>
        ) : (
          <div className="space-y-2.5">
            {familles.map((f) => (
              <div key={f.cle} className="flex items-center gap-3" data-testid={`barre-${f.cle}`}>
                <span className="text-xs w-28 shrink-0">{famille(f.cle)}</span>
                <div className="flex-1 h-5 rounded bg-muted relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${(f.total / maxBarre) * 100}%`, background: f.couleur, opacity: 0.35 }} />
                  <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${(f.passees / maxBarre) * 100}%`, background: f.couleur }} />
                </div>
                <span className="text-xs font-mono tabular-nums text-muted-foreground w-10 text-right shrink-0">{f.total}</span>
                <span className="text-xs font-mono tabular-nums w-12 text-right shrink-0">
                  {f.total ? new Intl.NumberFormat(i18n.language, { style: 'percent' }).format(f.passees / f.total) : '—'}
                </span>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground pt-1">{t('pages:objections.chart.legend')}</p>
          </div>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-2" data-testid="objections-filtres">
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger className={selectCls} aria-label={t('pages:objections.filters.type')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('pages:objections.filters.allTypes')}</SelectItem>
            {FAMILLES.map((f) => <SelectItem key={f.cle} value={f.cle}>{famille(f.cle)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatut} onValueChange={setFStatut}>
          <SelectTrigger className={selectCls} aria-label={t('pages:objections.filters.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('pages:objections.filters.allStatuses')}</SelectItem>
            {STATUTS.map((s) => <SelectItem key={s.cle} value={s.cle}>{t(s.libelleKey)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fAgent} onValueChange={setFAgent}>
          <SelectTrigger className={selectCls} aria-label={t('pages:objections.filters.user')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('pages:objections.filters.allUsers')}</SelectItem>
            {agents.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        {listes.length > 0 && (
          <Select value={fListe} onValueChange={setFListe}>
            <SelectTrigger className={selectCls} aria-label={t('pages:objections.filters.list')}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('pages:objections.filters.allLists')}</SelectItem>
              {listes.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input type="number" min="0" className="h-9 w-28" placeholder={t('pages:objections.filters.minDuration')}
          value={dureeMin} onChange={(e) => setDureeMin(e.target.value)} aria-label={t('pages:objections.filters.minDurationAria')} />
        <Input type="number" min="0" className="h-9 w-28" placeholder={t('pages:objections.filters.maxDuration')}
          value={dureeMax} onChange={(e) => setDureeMax(e.target.value)} aria-label={t('pages:objections.filters.maxDurationAria')} />
        <Button variant="outline" size="sm" className="h-9 ml-auto gap-1.5" onClick={exporter} disabled={!filtrees.length}>
          <Download className="w-3.5 h-3.5" aria-hidden />
          {t('pages:objections.exportCsv')}
        </Button>
      </div>

      <section aria-labelledby="objections-table-title" className="space-y-2">
        <div className="flex items-center gap-2">
          <h2 id="objections-table-title" className="text-sm font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('pages:objections.table.title')}</h2>
          <span className="ml-auto text-xs text-muted-foreground" data-testid="objections-compte">
            {t('pages:objections.table.countOf', { count: filtrees.length, total: toutes.length })}
          </span>
        </div>
        <DataTable
          caption={t('pages:objections.table.title')}
          columns={columns}
          rows={rowsPage}
          getRowId={(o) => o.key}
          status={analysis.isPending ? 'loading' : analysis.isError ? 'error' : 'ready'}
          error={{ message: t('pages:objections.loadError'), code: errorCode, onRetry: () => void analysis.refetch() }}
          filters={pills}
          onClearFilters={clearFilters}
          empty={fPeriode === 'all'
            ? { title: t('pages:objections.table.none'), action: <Button size="sm" onClick={() => navigate('/ParallelDialer')}>{t('pages:objections.table.call')}</Button> }
            : { title: t('pages:objections.table.noneInPeriod'), action: <Button size="sm" variant="outline" onClick={() => setFPeriode('all')}>{t('pages:objections.table.allPeriod')}</Button> }}
          pagination={{ page, pageSize: PAGE_SIZE, total: filtrees.length, onPageChange: setPage }}
          storageKey="objections"
        />
      </section>
    </div>
  );
}
